// src/components/dashboard/admin/AdminUsers.tsx
// ✅ ALIGNED: Consistent with all other Nexilink pages
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/contexts/AdminContext';
import { OrgLogo } from '@/components/custom-ui/OrgLogo';
import { AdminList, AdminListRow } from './AdminList';
import { useOrganisationLookup } from '@/hooks/useOrganisationLookup';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Users,
    GraduationCap,
    Briefcase,
    Shield,
    RefreshCw,
    Building2,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { resolveMediaUrl } from '@/lib/mediaUrl';

// Import Nexilink components
import {
    OutlineButton,
    EmptyState,
    StatusBadge,
} from '@/components/custom-ui';

const AdminUsers: React.FC = () => {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const {
        users,
        isLoadingUsers,
        usersError,
        loadUsers,
    } = useAdmin();

    // #354 — resolves an employer's organisation (and therefore its logo) from the
    // org list AdminContext already holds; the users response carries only the id
    // and name.
    const orgFor = useOrganisationLookup();

    const [roleFilter, setRoleFilter] = useState<string>('');

    useEffect(() => {
        loadUsers();
    }, []);

    const handleRoleFilterChange = (value: string) => {
        const filterValue = value === 'all' ? '' : value;
        setRoleFilter(filterValue);
        loadUsers(filterValue);
    };

    const handleRefresh = () => {
        loadUsers(roleFilter);
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'candidate':
                return <GraduationCap className="h-4 w-4" />;
            case 'employer':
                return <Briefcase className="h-4 w-4" />;
            case 'admin':
                return <Shield className="h-4 w-4" />;
            default:
                return <Users className="h-4 w-4" />;
        }
    };

    const getRoleBadgeVariant = (role: string) => {
        switch (role) {
            case 'admin':
                return 'destructive';
            case 'employer':
                return 'default';
            case 'candidate':
                return 'secondary';
            default:
                return 'outline';
        }
    };

    const getRoleLabel = (role: string) => {
        switch (role) {
            case 'candidate':
                return 'Kandidat';
            case 'employer':
                return 'Arbetsgivare';
            case 'admin':
                return 'Administratör';
            default:
                return role;
        }
    };

    return (
        <div className="space-y-6">
            {/* Title is in the top header strip (TopHeader). #358 — same AdminList
                shell as Organisationer and Kontaktförfrågningar; this list keeps
                its own role filter and its richer employer-context column. */}
            <AdminList
                title="Användare"
                count={users.length}
                isLoading={isLoadingUsers}
                // #353 — until now a failed load rendered "Inga användare hittades",
                // so a dead backend and an empty platform looked the same.
                error={usersError}
                onRetry={handleRefresh}
                filter={
                    <Select value={roleFilter || 'all'} onValueChange={handleRoleFilterChange}>
                        <SelectTrigger className="w-[190px]">
                            <SelectValue placeholder="Filtrera på roll" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alla roller</SelectItem>
                            <SelectItem value="candidate">Kandidater</SelectItem>
                            <SelectItem value="employer">Arbetsgivare</SelectItem>
                            <SelectItem value="admin">Administratörer</SelectItem>
                        </SelectContent>
                    </Select>
                }
                action={
                    <OutlineButton onClick={handleRefresh} disabled={isLoadingUsers} icon={RefreshCw}>
                        Uppdatera
                    </OutlineButton>
                }
                empty={
                    <EmptyState
                        icon={Users}
                        title="Inga användare hittades"
                        description="Inga användare matchar de valda filtren."
                    />
                }
            >
                {users.map((user) => {
                    const isCurrentUser = currentUser?.id === user.id;
                    return (
                        <AdminListRow
                            key={user.id}
                            leading={
                                <Avatar className="w-11 h-11">
                                    <AvatarImage src={resolveMediaUrl(user.picture_url)} />
                                    <AvatarFallback className="bg-[#A4C3B2] text-[#1A4D4F]">
                                        {user.initials}
                                    </AvatarFallback>
                                </Avatar>
                            }
                            title={
                                <span className="flex items-center gap-2">
                                    <span className="truncate">{user.full_name}</span>
                                    {isCurrentUser && <Badge variant="outline" className="text-xs">Du</Badge>}
                                </span>
                            }
                            subtitle={user.email}
                            trailing={
                                <>
                                    {/* Employer context — unit + org, for employer users only. */}
                                    <div className="hidden md:flex flex-col text-sm min-w-[180px] max-w-[240px]">
                                        {user.employer ? (
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/dashboard/organisations/${user.employer!.organisation_id}`)}
                                                className="text-left group"
                                                title={`${user.employer.unit_name} · ${user.employer.organisation_name}`}
                                            >
                                                <div className="flex items-center gap-1.5 font-medium text-[#1A4D4F] group-hover:underline truncate">
                                                    <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
                                                    <span className="truncate">{user.employer.unit_name}</span>
                                                </div>
                                                {/* #354 — the org line names a SPECIFIC organisation, so it
                                                    shows that org's logo. The unit line above keeps Briefcase:
                                                    a unit has no identity of its own to show. */}
                                                <div className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
                                                    <OrgLogo
                                                        name={user.employer.organisation_name}
                                                        logoUrl={orgFor(user.employer.organisation_id)?.logo_url}
                                                        size="xs"
                                                    />
                                                    <span className="truncate">{user.employer.organisation_name}</span>
                                                </div>
                                            </button>
                                        ) : (
                                            <span className="text-xs text-muted-foreground italic">—</span>
                                        )}
                                    </div>
                                    <Badge
                                        variant={getRoleBadgeVariant(user.role) as any}
                                        className="flex items-center gap-1 flex-shrink-0"
                                    >
                                        {getRoleIcon(user.role)}
                                        {getRoleLabel(user.role)}
                                    </Badge>
                                    <StatusBadge
                                        status={user.is_verified ? 'active' : 'pending'}
                                        label={user.is_verified ? 'Verifierad' : 'Ej verifierad'}
                                        className="flex-shrink-0"
                                    />
                                    <div className="hidden lg:block text-xs text-gray-400 min-w-[110px] text-right flex-shrink-0">
                                        {new Date(user.created_at).toLocaleDateString('sv-SE')}
                                    </div>
                                </>
                            }
                        />
                    );
                })}
            </AdminList>
        </div>
    );
};

export default AdminUsers;